import React, { useState, useEffect } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../data/translations';
import { getSiteConfig, SiteConfig } from '../data/siteConfig';
import { getLogoImageSeo } from '../utils/imageSeo';
import { Flame, Phone, Mail, MapPin, ShieldCheck, Award, Lock } from 'lucide-react';

interface FooterProps {
  currentLang: Language;
  setView: (view: string) => void;
  onOpenSchema: () => void;
}

export const Footer: React.FC<FooterProps> = ({ currentLang, setView, onOpenSchema }) => {
  const t = TRANSLATIONS[currentLang];
  const [config, setConfig] = useState<SiteConfig>(getSiteConfig());

  useEffect(() => {
    const handleConfigUpdate = () => {
      setConfig(getSiteConfig());
    };
    window.addEventListener('siteConfigUpdated', handleConfigUpdate);
    return () => window.removeEventListener('siteConfigUpdated', handleConfigUpdate);
  }, []);

  return (
    <footer className="bg-gray-900 text-gray-300 pt-16 pb-12 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Col 1: Brand & Bio */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {config.headerLogo ? (
                <img
                  src={config.headerLogo}
                  alt={getLogoImageSeo(currentLang).alt}
                  title={getLogoImageSeo(currentLang).title}
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="w-10 h-10 rounded-xl object-contain bg-white border border-gray-700 p-0.5 shadow-sm"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md">
                  <Flame className="w-5 h-5 text-yellow-300" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xl font-black text-white tracking-tight">API HEAT</span>
                  <span className="text-sm">🔴🔵</span>
                </div>
                <span className="text-xs text-orange-400 font-bold">ELF DISTRICT</span>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">
              {config.footerBio[currentLang] || config.footerBio.fr}
            </p>
            <div className="pt-2 flex flex-wrap gap-2 text-xs text-gray-400">
              <div className="flex items-center gap-1 bg-gray-800 px-2.5 py-1 rounded-md">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>TVA: {config.vat}</span>
              </div>
              <div className="flex items-center gap-1 bg-gray-800 px-2.5 py-1 rounded-md">
                <Award className="w-4 h-4 text-blue-400" />
                <span>Dir: {config.director}</span>
              </div>
            </div>
          </div>

          {/* Col 2: Services */}
          <div className="space-y-4">
            <h3 className="text-white font-bold text-base tracking-wide border-l-2 border-blue-600 pl-3">
              {t.footerCol1}
            </h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>
                <button onClick={() => setView('services')} className="hover:text-white transition-colors">
                  {currentLang === 'fr' ? 'Plomberie & Sanitaire' : currentLang === 'nl' ? 'Loodgieterij & Sanitair' : 'Plumbing & Sanitary'}
                </button>
              </li>
              <li>
                <button onClick={() => setView('services')} className="hover:text-white transition-colors">
                  {currentLang === 'fr' ? 'Chauffage & Chaudières' : currentLang === 'nl' ? 'Verwarming & Ketels' : 'Heating & Boilers'}
                </button>
              </li>
              <li>
                <button onClick={() => setView('services')} className="hover:text-white transition-colors">
                  {currentLang === 'fr' ? 'Débouchage Urgent 24/7' : currentLang === 'nl' ? 'Dringende Ontstopping 24/7' : 'Emergency Drain Unblocking'}
                </button>
              </li>
              <li>
                <button onClick={() => setView('services')} className="hover:text-white transition-colors">
                  {currentLang === 'fr' ? 'Détection de fuite' : currentLang === 'nl' ? 'Lekdetectie' : 'Leak detection'}
                </button>
              </li>
            </ul>
          </div>

          {/* Col 3: Regions / Zones */}
          <div className="space-y-4">
            <h3 className="text-white font-bold text-base tracking-wide border-l-2 border-blue-600 pl-3">
              {t.footerCol2}
            </h3>
            <ul className="space-y-2 text-xs sm:text-sm text-gray-300">
              <li className="pb-1 border-b border-gray-800">
                <button onClick={() => setView('zone-bruxelles-19-communes')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer font-bold text-yellow-400">
                  <span>🏛️</span>
                  <span>{currentLang === 'fr' ? 'Bruxelles (Les 19 Communes)' : currentLang === 'nl' ? 'Brussel (De 19 Gemeenten)' : 'Brussels (All 19 Municipalities)'}</span>
                </button>
              </li>
              <li className="pb-1 border-b border-gray-800">
                <button onClick={() => setView('zone-brabant-flamand')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer font-bold text-yellow-400">
                  <span>🏭</span>
                  <span>{currentLang === 'fr' ? 'Brabant Flamand & Ring (Siège Leeuw)' : currentLang === 'nl' ? 'Vlaams-Brabant & Rand (Zetel Leeuw)' : 'Flemish Brabant & Ring (HQ Leeuw)'}</span>
                </button>
              </li>
              <li className="pb-1 border-b border-gray-800">
                <button onClick={() => setView('zone-brabant-wallon')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer font-bold text-yellow-400">
                  <span>🏡</span>
                  <span>{currentLang === 'fr' ? 'Brabant Wallon (Waterloo, Wavre)' : currentLang === 'nl' ? 'Waals-Brabant (Waterloo, Waver)' : 'Walloon Brabant (Waterloo, Wavre)'}</span>
                </button>
              </li>
              <li>
                <button onClick={() => setView('zone-bruxelles')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer">
                  <span>📍</span>
                  <strong className="text-gray-200 hover:text-orange-400">Bruxelles (1000) :</strong> 24/7 en 20-30 min
                </button>
              </li>
              <li>
                <button onClick={() => setView('zone-sint-pieters-leeuw')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer">
                  <span>📍</span>
                  <strong className="text-gray-200 hover:text-orange-400">Sint-Pieters-Leeuw (1600 - Siège) :</strong> 15-20 min
                </button>
              </li>
              <li>
                <button onClick={() => setView('zone-ixelles')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer">
                  <span>📍</span>
                  <strong className="text-gray-200 hover:text-orange-400">Ixelles / Elsene (1050) :</strong> Flagey, Châtelain
                </button>
              </li>
              <li>
                <button onClick={() => setView('zone-uccle')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer">
                  <span>📍</span>
                  <strong className="text-gray-200 hover:text-orange-400">Uccle / Ukkel (1180) :</strong> Churchill, Fort Jaco
                </button>
              </li>
              <li>
                <button onClick={() => setView('zone-anderlecht')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer">
                  <span>📍</span>
                  <strong className="text-gray-200 hover:text-orange-400">Anderlecht (1070) :</strong> Dépannage express
                </button>
              </li>
              <li>
                <button onClick={() => setView('zone-waterloo')} className="hover:text-orange-400 transition-colors text-left flex items-center gap-1.5 cursor-pointer">
                  <span>📍</span>
                  <strong className="text-gray-200 hover:text-orange-400">Waterloo (1410) & BW :</strong> 24/7
                </button>
              </li>
              <li className="pt-1">
                <button onClick={() => setView('zones')} className="text-orange-400 hover:underline text-xs font-bold flex items-center gap-1 cursor-pointer">
                  <span>{currentLang === 'fr' ? '→ Voir toutes les 589 communes de Belgique' : currentLang === 'nl' ? '→ Bekijk alle 589 gemeenten in België' : '→ View all 589 Belgian municipalities'}</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Col 4: Contact & SEO */}
          <div className="space-y-4">
            <h3 className="text-white font-bold text-base tracking-wide border-l-2 border-blue-600 pl-3">
              {t.footerCol3}
            </h3>
            <ul className="space-y-2.5 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-400 shrink-0" />
                <div className="flex flex-col">
                  <a
                    href={`tel:${config.gsm.replace(/[^0-9+]/g, '')}`}
                    aria-label={`Appeler API HEAT au ${config.gsm}`}
                    className="hover:text-white font-semibold text-white min-h-[36px] flex items-center"
                  >
                    {config.gsm}
                  </a>
                  <span className="text-xs text-orange-400 font-bold">
                    Urgence 24/7 ({config.director})
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                <a href={`mailto:${config.email}`} className="text-gray-300 hover:text-white text-xs hover:underline transition" title="Envoyer un e-mail">
                  {config.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(config.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-white hover:underline transition leading-snug"
                  title="Ouvrir dans Google Maps / GPS"
                >
                  {config.address}
                </a>
              </li>
              <li className="pt-2 flex flex-wrap gap-2">
                <a
                  href="https://share.google/fEyXZvFgRSHJqZECr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 px-3 py-1.5 rounded-lg border border-blue-500/30 transition flex items-center gap-1.5 font-bold"
                >
                  <span>⭐ Google My Business</span>
                </a>
                <button
                  onClick={onOpenSchema}
                  className="text-xs bg-gray-800 hover:bg-gray-700 text-yellow-300 px-3 py-1.5 rounded-lg border border-yellow-500/20 transition flex items-center gap-1.5"
                >
                  <span>🔍 JSON-LD</span>
                </button>
                <button
                  onClick={() => setView('admin')}
                  className="text-xs bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 px-3 py-1.5 rounded-lg border border-orange-500/30 transition flex items-center gap-1.5 font-bold"
                >
                  <Lock className="w-3.5 h-3.5" />
                  <span>Admin</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO Keywords Tag Cloud */}
        <div className="border-t border-gray-800 pt-8 pb-6">
          <p className="text-xs text-gray-300 mb-3 uppercase tracking-wider font-semibold">
            {currentLang === 'fr' ? 'Mots-clés recherchés en Belgique :' : currentLang === 'nl' ? 'Populaire zoektermen in België:' : 'Popular Belgian Search Keywords:'}
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-gray-300">
            <span className="bg-gray-800 px-2.5 py-1 rounded">plombier Belgique</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">chauffagiste Bruxelles</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">loodgieter Sint-Pieters-Leeuw</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">loodgieter Antwerpen</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">loodgieter Gent & Leuven</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">chauffagist Halle & Vilvoorde</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">onderhoud gasketel Vlaanderen</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">ontstoppingsdienst Vlaams-Brabant</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">emergency plumber Belgium</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">heating engineer Brussels</span>
            <span className="bg-gray-800 px-2.5 py-1 rounded">drain unblocking service Belgium</span>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800/60 pt-6 flex flex-col sm:flex-row justify-between items-center text-xs text-gray-300 gap-4">
          <p>© {new Date().getFullYear()} API HEAT. {t.footerRights}</p>
          <div className="flex items-center gap-6">
            <span className="hover:text-white cursor-pointer">Privacy & Cookies</span>
            <span className="hover:text-white cursor-pointer">Mentions Légales</span>
            <span className="hover:text-white cursor-pointer">Agréé dans toute la Belgique</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
